import React, { useEffect, useMemo, useState } from "react";
import {
  PageList,
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
} from "../../components/pageList";
import ActionCellRender from "../../components/actionCellRender";
import { useCellRender } from "../../hooks/useCellRender";
import { IconPencil, IconSettings } from "@tabler/icons";
import { useAuthUser } from "react-auth-kit";
import moment from "moment";
import { Alert } from "@mantine/core";

import PayrollPaymentInfo from "../../components/payrollPaymentInfo";
import PayrollPaymentBids from "../../components/payrollPaymentBids";
import PayrollPaymentRefers from "../../components/payrollPaymentRefers";
import { useServerApi } from "../../hooks/userServerApi";

const PayrollPayment = ({}) => {
  const apiEntity = "payrollPayment";
  const [cellRender] = useCellRender();
  const auth = useAuthUser();
  const [api] = useServerApi();
  const userId = auth()?.id || auth()?._id;
  const [walletIds, setWalletIds] = useState([]);
  const [walletLookupDone, setWalletLookupDone] = useState(false);
  const [walletLookupError, setWalletLookupError] = useState(false);

  useEffect(() => {
    if (!userId) {
      setWalletIds([]);
      setWalletLookupDone(true);
      return;
    }
    const fetchWallets = async () => {
      try {
        setWalletLookupDone(false);
        setWalletLookupError(false);
        const result = await api.search({
          apiEntity: "wallet",
          searchQuery: {
            $or: [{ user: userId }, { "members.user": userId }],
          },
        });
        const ids = (result.docs || []).map((w) => w._id);
        setWalletIds((prev) =>
          prev.length === ids.length && prev.every((id, i) => id === ids[i])
            ? prev
            : ids,
        );
        setWalletLookupDone(true);
      } catch (error) {
        setWalletIds([]);
        setWalletLookupError(true);
        setWalletLookupDone(true);
      }
    };
    fetchWallets();
    // api is recreated each render by useServerApi; userId is the real trigger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const payrollPaymentPreQuery = useMemo(() => {
    if (!walletLookupDone) return {};
    if (walletIds.length === 0) return { wallet: { $in: [] } };

    return {
      wallet: walletIds.length === 1 ? walletIds[0] : { $in: walletIds },
    };
  }, [walletIds, walletLookupDone]);

  const tableSchema = useMemo(
    () => ({
      showActionColumn: true,
      preQuery: payrollPaymentPreQuery,
      columns: [
        {
          field: "createdAt",
          headerName: "Date",
          sortable: true,
          cellRender: (col, data) => moment(data.createdAt).format("ll"),
        },
        { field: "code", headerName: "Code", sortable: true },

        {
          field: "bids",
          headerName: "Bids",
          sortable: true,
          cellRender: (col, data) =>
            data.totalBids ??
            (data.payrollBids?.length > 0
              ? data.payrollBids.length
              : data.bids?.length ?? 0),
        },

        {
          field: "totalRefer",
          headerName: "Refer Payment",
          sortable: true,
          cellRender: (col, data) => cellRender.Currency(data.totalRefer),
        },
        {
          field: "totalSelf",
          headerName: "Consultant Payment",
          sortable: true,
          cellRender: (col, data) => cellRender.Currency(data.totalSelf),
        },
        {
          field: "total",
          headerName: "Total Payment",
          sortable: true,
          cellRender: (col, data) => cellRender.Currency(data.total),
        },

        {
          field: "status",
          cellRender: (col, data) => cellRender.Status(data.status),
        },

        {
          field: "action",
          cellRender: ActionCellRender,
          isAction: true,
          actions: [
            {
              name: "edit",
              icon: IconPencil,
              props: {},
            },
          ],
          actionMenu: [],
        },
      ],
    }),
    // cellRender is recreated each render; payrollPaymentPreQuery is the meaningful dep
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payrollPaymentPreQuery],
  );

  const formSchema = {
    titleField: "code",
    display: {
      mode: ENUM_FORM_DISPLAY.LINK,
      size: "xl",
    },
    apiEntity,

    layout: {
      containers: [
        {
          key: "section-info",
          type: ENUM_FORM_LAYOUT_CONTAINER.BOX,
          parent: null,
          props: {
            title: "Payroll Info",
            mt: "xl",
          },
        },

        {
          key: "main-tab",
          parent: null,
          type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
          props: {
            variant: "default",
            defaultValue: "Bid Details",
            mt: "xl",
          },
          tab: [
            {
              key: "tab-bids",
              label: "Bid Details",
              icon: IconSettings,
            },
            {
              key: "tab-referal",
              label: "Refers",
              icon: IconSettings,
            },
          ],
        },
      ],

      fields: [
        {
          name: "info",
          parent: "section-info",
          component: PayrollPaymentInfo,
          props: {},
        },
        {
          name: "info",
          parent: "tab-bids",
          component: PayrollPaymentBids,
          props: {},
        },

        {
          name: "info",
          parent: "tab-referal",
          component: PayrollPaymentRefers,
          props: {},
        },
      ],
    },
  };
  const showNoWalletNotice = walletLookupDone && walletIds.length === 0;

  return (
    <>
      {showNoWalletNotice && (
        <Alert color={walletLookupError ? "red" : "yellow"} mb="md">
          No payroll wallet found.
        </Alert>
      )}
      <PageList
        title={"My Payroll"}
        initSort={{ by: "createdAt", order: "desc" }}
        apiEntity={apiEntity}
        tableSchema={tableSchema}
        formSchema={formSchema}
        showAddButton={false}
        readOnly={true}
        preQueryNotEmpty={true}
      />
    </>
  );
};

export default PayrollPayment;
